import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { LanguageContext } from '../LanguageContext';
import api from '../services/api';
import GaugeChartComponent from './GaugeChartComponent';
import LineChartComponent from './LineChartComponent';
import PieChartComponent from './PieChartComponent';
import styles from './Dashboard.module.css';

const KioskActivate = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    const activate = async () => {
      try {
        const response = await api.post('/kiosk/activate/', { token }, { withCredentials: true });
        navigate(`/kiosk/${response.data.slug}`, { replace: true });
      } catch (e) {
        setError('Activation failed. Ask admin for a new kiosk link. Please contact +48 732022246 for support.<br>Aktywacja nie powiodła się. Poproś administratora o nowy link do kiosku. Skontaktuj się z +48 732022246 w celu wsparcia.<br>Aktivierung fehlgeschlagen. Bitten Sie einen Administrator um einen neuen Kiosk-Link. Kontaktieren Sie +48 732022246 für Unterstützung.');
      }
    };
    activate();
  }, [token, navigate]);

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
      <div>
        <h2>Kiosk activation</h2>
        <p dangerouslySetInnerHTML={{ __html: error || 'Activating kiosk session...' }} />
      </div>
    </div>
  );
};

const KioskDashboard = () => {
  const getCompactLayout = () => window.innerWidth < 1100 || window.innerHeight > window.innerWidth;
  const { slug } = useParams();
  const { language, setLanguage, t } = useContext(LanguageContext);

  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [isCompactLayout, setIsCompactLayout] = useState(getCompactLayout());
  const [error, setError] = useState('');

  useEffect(() => {
    const handleResize = () => setIsCompactLayout(getCompactLayout());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const response = await api.get(`/kiosk/${slug}/me/`, { withCredentials: true });
        setDevices(response.data.devices || []);
        if ((response.data.devices || []).length > 0) {
          setSelectedDevice(response.data.devices[0].id);
        }
      } catch (e) {
        setError('Kiosk session is invalid or expired. Use activation link again.');
      }
    };
    fetchDevices();
    const timer = setInterval(fetchDevices, 300000);
    return () => clearInterval(timer);
  }, [slug]);

  useEffect(() => {
    if (selectedDevice === null) {
      return;
    }

    const fetchData = async () => {
      try {
        try {
          const response = await api.get(`/kiosk/${slug}/devices/${selectedDevice}/charts-v3/`, { withCredentials: true });
          setChartData(response.data);
        } catch (v3Error) {
          const response = await api.get(`/kiosk/${slug}/devices/${selectedDevice}/charts/`, { withCredentials: true });
          setChartData(response.data);
        }
        setError('');
      } catch (e) {
        setError('Cannot load kiosk data.');
      }
    };

    fetchData();
    const timer = setInterval(fetchData, 300000);
    return () => clearInterval(timer);
  }, [selectedDevice, slug]);

  const stateLabels = useMemo(() => ({
    0: t('no_proces'),
    1: t('process_recognition'),
    2: t('r_proces'),
    3: t('process_unrecognized'),
    4: t('cleaning'),
    5: t('cleaning'),
    6: t('off'),
    7: t('off'),
  }), [t]);
  const leafletHref = {
    pl: `${process.env.PUBLIC_URL || ''}/leaflets/ulotka-pl.pdf`,
    en: `${process.env.PUBLIC_URL || ''}/leaflets/leaflet-en.pdf`,
    de: `${process.env.PUBLIC_URL || ''}/leaflets/flyer-de.pdf`,
  }[language] || `${process.env.PUBLIC_URL || ''}/leaflets/ulotka-pl.pdf`;

  const displayDevices = devices.length > 0 ? devices : [{ id: 0, name: t('loading') }];
  const currentSelectedDevice = devices.length > 0 ? selectedDevice : null;
  const tileStyle = { minHeight: 0, overflow: 'hidden' };
  const currentFilterUsage = Math.max(0, Number(chartData.hepa_actual || 0));

  const isOlderThan6Hours = (timestamp) => {
    if (!timestamp) return false;
    const now = new Date();
    const lastTime = new Date(timestamp);
    const diffInHours = (now - lastTime) / (1000 * 60 * 60);
    return diffInHours > 6;
  };

  const getTimeSinceLastCommunication = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const lastTime = new Date(timestamp);
    const diffInHours = (now - lastTime) / (1000 * 60 * 60);

    if (diffInHours < 6) return '';

    if (diffInHours >= 24) {
      const days = Math.floor(diffInHours / 24);
      const hours = Math.floor(diffInHours % 24);
      if (days === 1) {
        return hours > 0 ? ` (${t('noCommunicationSince')} ${days} ${t('day')} ${hours} ${getHourUnit(hours)})` : ` (${t('noCommunicationSince')} ${days} ${t('day')})`;
      }
      return hours > 0 ? ` (${t('noCommunicationSince')} ${days} ${t('days')} ${hours} ${getHourUnit(hours)})` : ` (${t('noCommunicationSince')} ${days} ${t('days')})`;
    }

    const hours = Math.floor(diffInHours);
    return ` (${t('noCommunicationSince')} ${hours} ${getHourUnit(hours)})`;
  };

  const formatLastTimestamp = (timestamp) => {
    if (!timestamp) return t('loading');
    const parsed = new Date(timestamp);
    if (Number.isNaN(parsed.getTime())) return timestamp;
    return parsed.toLocaleString();
  };

  const formatHoursValue = (value) => {
    if (value === undefined || value === null || value === '') {
      return t('loading');
    }

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return value;
    }

    const abs = Math.abs(numericValue);
    let unit = t('hourUnit_many');

    if (language === 'pl') {
      const mod10 = abs % 10;
      const mod100 = abs % 100;
      if (abs === 1) {
        unit = t('hourUnit_one');
      } else if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) {
        unit = t('hourUnit_few');
      }
    } else if (abs === 1) {
      unit = t('hourUnit_one');
    }

    return `${numericValue} ${unit}`;
  };

  const getHourUnit = (value) => {
    const abs = Math.abs(Number(value));
    let unit = t('hourUnit_many');

    if (language === 'pl') {
      const mod10 = abs % 10;
      const mod100 = abs % 100;
      if (abs === 1) {
        unit = t('hourUnit_one');
      } else if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) {
        unit = t('hourUnit_few');
      }
    } else if (abs === 1) {
      unit = t('hourUnit_one');
    }

    return unit;
  };

  const hasProcessStats = chartData && chartData.ProcessStats;
  const hasDiagnostics = chartData && chartData.CurrentDiagnostics;

  return (
    <div className={styles.dashboard} style={{
      display: 'flex',
      flexDirection: isCompactLayout ? 'column' : 'row',
      minHeight: '100vh',
      height: isCompactLayout ? 'auto' : '100vh',
      maxHeight: isCompactLayout ? 'none' : '100vh',
      alignItems: isCompactLayout ? 'stretch' : 'flex-start',
      overflow: isCompactLayout ? 'visible' : 'hidden',
    }}>
      <div className={styles.sidebar} style={{
        width: isCompactLayout ? '100%' : '250px',
        maxWidth: isCompactLayout ? 'none' : '250px',
        height: isCompactLayout ? 'auto' : '100%',
        minHeight: isCompactLayout ? 'auto' : 'unset',
        maxHeight: isCompactLayout ? 'none' : '100%',
        order: isCompactLayout ? 2 : 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: isCompactLayout ? 'visible' : 'hidden',
      }}>
        <div className={styles.userInfo}>
          <h3 style={{
            fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
            lineHeight: '1.2',
            wordWrap: 'break-word',
          }}>{t('userInfo')}</h3>
        </div>

        <div className={styles.deviceList}>
          <h4>{t('devices')}</h4>
          <ul>
            {displayDevices.map((device) => (
              <li
                key={device.id}
                className={currentSelectedDevice === device.id ? styles.activeDevice : ''}
                onClick={() => devices.length > 0 && setSelectedDevice(device.id)}
              >
                {device.name}
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.languageSelector}>
          <h4>{t('language')}</h4>
          <div className={styles.languageFlags}>
            <span
              className={`fi fi-gb ${language === 'en' ? styles.activeFlag : ''}`}
              onClick={() => setLanguage('en')}
              title="English"
            ></span>
            <span
              className={`fi fi-pl ${language === 'pl' ? styles.activeFlag : ''}`}
              onClick={() => setLanguage('pl')}
              title="Polski"
            ></span>
            <span
              className={`fi fi-de ${language === 'de' ? styles.activeFlag : ''}`}
              onClick={() => setLanguage('de')}
              title="Deutsch"
            ></span>
          </div>
          <a
            className={styles.kioskLeafletButton}
            href={leafletHref}
            target="_blank"
            rel="noreferrer"
          >
            <span className={styles.kioskLeafletEyebrow}>PDF</span>
            <span className={styles.kioskLeafletLabel}>{t('leaflet')}</span>
          </a>
        </div>

        {error && <div style={{ color: '#e40000ff', padding: '10px' }}>{error}</div>}
      </div>

      <div className={styles.mainContent} style={{
        flex: 1,
        padding: isCompactLayout ? '12px' : '20px',
        order: isCompactLayout ? 1 : 2,
        height: isCompactLayout ? 'auto' : '100%',
        overflow: isCompactLayout ? 'visible' : 'hidden',
        display: 'flex',
      }}>
        <div
          className={styles.tiles}
          style={{
            display: 'grid',
            gridTemplateColumns: isCompactLayout ? '1fr' : 'repeat(2, minmax(0, 1fr))',
            gridTemplateRows: isCompactLayout ? 'auto' : 'repeat(2, minmax(0, 1fr))',
            gap: isCompactLayout ? '14px' : '20px',
            height: isCompactLayout ? 'auto' : '100%',
            width: '100%',
            minHeight: 0,
          }}
        >
          <div className={styles.tile} style={isCompactLayout ? { ...tileStyle, minHeight: '320px' } : tileStyle}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>{t('hepa_filter')}</h3>
              <div style={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <GaugeChartComponent hepaValue={currentFilterUsage} />
              </div>
            </div>
          </div>

          <div className={styles.tile} style={isCompactLayout ? { ...tileStyle, overflow: 'visible', minHeight: '360px' } : { ...tileStyle, overflow: 'visible' }}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>{t('process_times')}</h3>
              <div style={{ flex: 1, minHeight: 0, width: '100%' }}>
                <PieChartComponent chartdata={chartData.DataTimes || {}} />
              </div>
            </div>
          </div>

          <div className={styles.tile} style={isCompactLayout ? { ...tileStyle, minHeight: '340px' } : tileStyle}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>{t('filter_usage_history')}</h3>
              <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', width: '100%' }}>
                <LineChartComponent
                  data={chartData.DataFilterUsage || []}
                  regressionData={chartData.DataFilterUsageRegression || []}
                  name={'filter_usage_history'}
                />
              </div>
            </div>
          </div>

          <div className={styles.tile} style={isCompactLayout ? { ...tileStyle, minHeight: '260px' } : tileStyle}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>{t('device_statistics')}</h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                flex: 1,
                fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                justifyContent: 'center',
              }}>
                <div>{t('allTime')}: {formatHoursValue(chartData.DataAllTime)}</div>
                <div>{t('fanTime')}: {formatHoursValue(chartData.DataFanTime)}</div>
                {hasProcessStats && <div>{t('completedProcesses')}: {chartData.ProcessStats.completed}</div>}
                {hasProcessStats && <div>{t('recognizedProcesses')}: {chartData.ProcessStats.recognized}</div>}
                {hasProcessStats && <div>{t('unrecognizedProcesses')}: {chartData.ProcessStats.unrecognized}</div>}
                {hasProcessStats && <div>{t('recognitionRate')}: {chartData.ProcessStats.recognition_rate}%</div>}
                <div style={{ color: isOlderThan6Hours(chartData.Last) ? '#e40000ff' : 'inherit' }}>
                  {t('last')}: {formatLastTimestamp(chartData.Last)}{getTimeSinceLastCommunication(chartData.Last)}
                </div>
                <div>{t('lastReadStatus')}: {chartData.DataStatusNow !== undefined ? stateLabels[chartData.DataStatusNow] || t('unknown') : t('loading')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { KioskActivate };
export default KioskDashboard;
